package com.sddamico.swigmore.ds.poll

import androidx.compose.runtime.Composable
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.SwEffects
import com.sddamico.swigmore.ds.SwType
import com.sddamico.swigmore.ds.core.Card
import com.sddamico.swigmore.ds.core.CardVariant
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeCaps
import com.sddamico.swigmore.ds.typeMono
import com.sddamico.swigmore.ds.typeTeam
import com.sddamico.swigmore.models.PollStanding
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** [readout] is on the right: points per game before kickoff, the score once released. */
data class MatchupSide(
  val team: String,
  val record: String,
  val standing: PollStanding,
  val readout: String? = null,
  val winner: Boolean = false,
)

/**
 * One game in "Saturday's Slate" or the results: two sides under a gold tag line, [tag] on the left
 * ("GAME OF THE WEEK") and [note] on the right (the kickoff day, or the result). The line shows when
 * either is set. Once a side is the [MatchupSide.winner], the other fades.
 */
@Composable
fun MatchupCard(
  tag: String,
  note: String,
  home: MatchupSide,
  away: MatchupSide,
  modifier: Modifier = Modifier,
  showReadouts: Boolean = true,
) {
  Card(modifier, variant = CardVariant.Field, padding = "12px 14px", radius = SwEffects.radiusCard) {
    if (tag.isNotBlank() || note.isNotBlank()) Div(
      attrs = Modifier.typeCaps("9px", ".18em")
        .css("display" to "flex", "justify-content" to "space-between", "color" to SwColor.gold, "margin-bottom" to "8px")
        .toAttrs()
    ) {
      Span { Text(tag) }
      Span(attrs = Modifier.typeMono("9px").css("color" to "rgba(239,229,201,.55)", "letter-spacing" to "0").toAttrs()) { Text(note) }
    }
    val sides = listOf(away, home)
    val showStanding = sides.any { it.standing != PollStanding.Unranked }
    val decided = sides.any { it.winner }
    sides.forEach { MatchupRow(it, showStanding, showReadouts, faded = decided && !it.winner) }
  }
}

@Composable
private fun MatchupRow(side: MatchupSide, showStanding: Boolean, showReadouts: Boolean, faded: Boolean) {
  Div(
    attrs = Modifier.css("display" to "grid",
      "grid-template-columns" to if (showStanding) "34px minmax(0,1fr) auto" else "minmax(0,1fr) auto",
      "align-items" to "center", "gap" to "10px", "padding" to "4px 0", "opacity" to if (faded) ".55" else "1").toAttrs()
  ) {
    if (showStanding) StandingCell(side.standing)
    Div(attrs = Modifier.css("min-width" to "0").toAttrs()) {
      Span(attrs = Modifier.typeTeam("14px").toAttrs()) { Text(side.team) }
      Text(" ")
      Span(attrs = Modifier.typeBody("12px", 500, "normal").css("color" to SwColor.textMuted, "white-space" to "nowrap").toAttrs()) {
        Text(side.record)
      }
    }
    if (showReadouts && side.readout != null) {
      Div(
        attrs = Modifier.typeMono("12px", if (side.winner) 700 else 400)
          .css("color" to if (side.winner) SwColor.gold else "rgba(239,229,201,.75)").toAttrs()
      ) { Text(side.readout) }
    }
  }
}

@Composable
private fun StandingCell(standing: PollStanding) {
  val (label, size, color) = when (standing) {
    is PollStanding.Ranked -> Triple(standing.rank.toString(), "22px", SwColor.cream)
    PollStanding.ReceivingVotes -> Triple("RV", "12px", SwColor.gold)
    PollStanding.Unranked -> Triple("", "12px", SwColor.textFaint)
  }
  Div(attrs = Modifier.css("font" to "400 $size/1 ${SwType.display}", "text-align" to "center", "color" to color).toAttrs()) { Text(label) }
}
